import { useMemo, useState } from 'react';
import { Rocket, Sparkles } from 'lucide-react';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { PostPreview, type PostPreviewData } from '@/components/PostPreview';
import { BoostSignalsPanel, InferenceNote } from './BoostSignalsPanel';
import { cn } from '@/lib/utils';
import { useBoostSignals, tierForScore } from '@/hooks/useBoostSignals';

export function CompetitorBoostsPage() {
  const [preview, setPreview] = useState<PostPreviewData | null>(null);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const { signals } = useBoostSignals(30);

  const tabs = useMemo(() => {
    const brandTabs = signals.map((s) => ({
      id: s.username,
      label: `@${s.username}`,
      tier: tierForScore(s.avgScore),
      avgScore: s.avgScore,
    }));
    return [{ id: 'summary', label: 'Summary', tier: 'unknown' as const, avgScore: 0 }, ...brandTabs];
  }, [signals]);

  return (
    <div className="space-y-4 md:space-y-6">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-rose-500" />
            Competitor boosts
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-2xs font-medium text-rose-700 dark:text-rose-300">
              <Sparkles className="h-3 w-3" /> inferred
            </span>
          </span>
        }
        description="Which competitors are pushing paid promotion vs. relying on organic reach. Inferred from post views ÷ followers on Instagram — no Ad Library dependency."
      />

      <InferenceNote />

      <div className="-mx-1 flex snap-x gap-1 overflow-x-auto border-b border-border/40 pb-1 pl-1">
        {tabs.map((t) => {
          const active = activeTab === t.id;
          const tierDot =
            t.id === 'summary'
              ? ''
              : t.tier === 'likely'
                ? 'bg-rose-500'
                : t.tier === 'possible'
                  ? 'bg-amber-500'
                  : t.tier === 'organic'
                    ? 'bg-emerald-500'
                    : 'bg-muted-foreground';
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={cn(
                'flex shrink-0 snap-start items-center gap-1.5 whitespace-nowrap rounded-t-md px-3 py-1.5 text-2xs font-medium transition',
                active
                  ? 'border-x border-t border-border/60 bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {tierDot && <span className={cn('h-1.5 w-1.5 rounded-full', tierDot)} />}
              {t.label}
              {t.id !== 'summary' && (
                <span className="tabular-nums text-muted-foreground">
                  {t.avgScore.toFixed(0)}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      <BoostSignalsPanel
        onPreview={setPreview}
        onlyHandle={activeTab === 'summary' ? undefined : activeTab}
        hideNote
      />

      <PostPreview post={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
